import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import TrustBlock from '../components/TrustBlock';
import HowItWorks from '../components/HowItWorks';
import Testimonials from '../components/Testimonials';
import LeadForm from '../components/LeadForm';
import { trackPageView } from '../lib/attribution';
import { serviceList, leadFormFields } from '../data/siteContent';

export default function Home() {
  const formRef = useRef(null);

  useEffect(() => {
    trackPageView('funnel_tax_main', 'page_home');
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '1,200+', label: 'Clients Served' },
    { value: '$50M+', label: 'Tax Savings Delivered' },
    { value: '98%', label: 'Client Satisfaction' },
  ];

  const partners = ['Pfister Financial', 'Strategic Tax', 'Fidelity Investments'];

  const steps = [
    {
      title: 'Discovery',
      description: 'We analyze your financial situation, business structure, and current tax strategy.',
    },
    {
      title: 'Strategy Design',
      description: 'Our experts design a customized tax strategy using IRS-compliant methods.',
    },
    {
      title: 'Coordination',
      description: 'We work with your existing CPA to ensure seamless implementation.',
    },
    {
      title: 'Implementation',
      description: 'Execute the strategy and track your savings year over year.',
    },
  ];

  const testimonials = [
    {
      quote: 'Crown Tax helped us reduce our effective tax rate from 37% to 22% without any aggressive schemes. Everything was transparent and compliant.',
      author: 'Sarah Mitchell',
      role: 'CEO',
      company: 'Mitchell & Associates',
    },
    {
      quote: 'They worked seamlessly with our existing CPA. No drama, no audit risk, just real savings that we can see every quarter.',
      author: 'David Chen',
      role: 'Managing Partner',
      company: 'Chen Law Group',
    },
    {
      quote: "Unlike other firms that promise the moon, Crown Tax delivers realistic, sustainable tax strategies. We've saved over $200K in two years.",
      author: 'Jennifer Torres',
      role: 'CFO',
      company: 'Torres Enterprises',
    },
  ];

  const branding = {
    primary_color: '#1e40af',
    secondary_color: '#AEFFE3',
  };

  return (
    <div className="min-h-screen bg-white">
      <HeroSection
        headline="Advanced Tax Strategies Without Aggressive Schemes or Audit Nightmares"
        subheadline="Legally reduce your effective tax rate using IRS-compliant strategies that your CPA will actually approve. No ERC mills, no fake dependents, no audit risk."
        ctaText="Get My Tax Strategy Assessment"
        onCtaClick={scrollToForm}
        branding={branding}
      />

      <TrustBlock stats={stats} partners={partners} branding={branding} />
      <HowItWorks steps={steps} branding={branding} />

      <section className="bg-gray-warm py-20 px-6">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <p className="text-sm uppercase tracking-[0.5em] text-gray-500">strategies we deploy</p>
            <h2 className="text-3xl font-bold text-charcoal">How we guide high-income earners</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {serviceList.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="group block rounded-3xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-lg transition"
              >
                <h3 className="text-xl font-semibold text-charcoal group-hover:text-primary">
                  {service.title}
                </h3>
                <p className="text-gray-600 mt-3 text-sm leading-relaxed">{service.summary}</p>
                <p className="mt-4 text-xs uppercase text-primary tracking-wide">See full practice →</p>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-500">Prefer to talk now?</p>
            <Link
              to="/schedule-call"
              className="inline-flex mt-3 items-center gap-3 rounded-full border border-primary px-5 py-2 text-sm font-semibold text-primary hover:bg-primary/5"
            >
              Schedule a call
            </Link>
          </div>
        </div>
      </section>

      <Testimonials testimonials={testimonials} branding={branding} />

      <section className="bg-white py-20 px-6">
        <div className="max-w-6xl mx-auto flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-gray-500">lead funnel</p>
            <h2 className="text-3xl font-bold text-charcoal">Ready for your strategy assessment?</h2>
            <p className="mt-3 text-gray-600">
              We combine CPA-level rigor with modular implementations that fit your advisor network. Fill the quick assessment and we’ll send you a strategy summary within 24 hours.
            </p>
          </div>
          <button
            onClick={scrollToForm}
            className="rounded-full bg-primary px-5 py-3 text-white font-semibold shadow-xl shadow-primary/40 transition hover:brightness-110"
          >
            Jump to assessment
          </button>
        </div>
      </section>

      <div ref={formRef}>
        <LeadForm
          funnelId="funnel_tax_main"
          pageId="home"
          ctaId="cta_home_assessment"
          fields={leadFormFields}
          branding={branding}
        />
      </div>
    </div>
  );
}
